import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PrintBrandingFooter — hidden on screen, visible only in print.
 * Renders a footer at the bottom of the printed PDF using the tenant's
 * company name, uploaded logo, accent colour, AND display/body font
 * families so the exported doc matches the on-screen brand. Falls back
 * to the generic "Yabbit VoiceFabric" branding when the user has no
 * domain (master admin) or the tenant hasn't uploaded custom branding.
 *
 * When present, this footer supersedes the CSS `::after` fallback in
 * `index.css` — that fallback is suppressed via a `.has-custom-print-brand`
 * modifier class on <body>.
 *
 * Font handling
 * ─────────────
 * The tenant's fonts are already loaded at login by AuthContext (via
 * `applyTenantTheme` → `applyTenantFonts` → dynamic @font-face). So the
 * browser Print dialog inherits them for free. We simply resolve the
 * FIRST quoted family from the stack ("AW Conqueror Sans" → "AW
 * Conqueror Sans") and set it as an inline `fontFamily` on the footer,
 * with a system-serif fallback so the doc never renders blank if the
 * face fails to load.
 */

// Extract the first quoted family from a CSS font-family stack — same
// helper as `applyTenantFonts` in `lib/tenantTheme.js`. Returns null for
// empty input so callers can fall through to defaults cleanly.
const primaryFamily = (raw) => {
  if (!raw) return null;
  const m = raw.match(/"([^"]+)"/) || raw.match(/'([^']+)'/);
  if (m) return m[1];
  return raw.split(',')[0].trim() || null;
};

const PrintBrandingFooter = () => {
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/branding/me`, { timeout: 10000 })
      .then((r) => { if (!cancelled) setBranding(r.data || null); })
      .catch(() => { if (!cancelled) setBranding(null); });
    return () => { cancelled = true; };
  }, []);

  const isCustom = !!branding && !branding.is_default && (
    branding.company_name || branding.custom_logo_url
  );

  // Toggle the body class the print stylesheet reads to suppress its
  // default footer. Effect runs on every render — safe because setting
  // the same class twice is a no-op.
  useEffect(() => {
    if (isCustom) {
      document.body.classList.add('has-custom-print-brand');
    } else {
      document.body.classList.remove('has-custom-print-brand');
    }
    return () => document.body.classList.remove('has-custom-print-brand');
  }, [isCustom]);

  if (!isCustom) return null;

  const accent = branding.custom_color || '#94a3b8';
  const label = branding.company_name || 'Yabbit VoiceFabric';

  // Resolve font stacks. Display font = the wordmark/label; body font =
  // "Call detail export" caption + surrounding meta. Fall back to the
  // full stack the tenant configured, or Outfit as final default.
  const displayStack = branding.custom_font_family_display
    || "'Outfit', system-ui, sans-serif";
  const bodyStack = branding.custom_font_family_body
    || displayStack;

  // Also derive the primary family name for `fontFamily` — some print
  // engines are picky about quoted stacks in inline styles. Keeping the
  // full stack works in Chrome/Edge PDF export, which is what "Download
  // PDF" uses.
  const displayPrimary = primaryFamily(displayStack) || 'Outfit';

  return (
    <div
      // Only appears in print media (Tailwind: `hidden print:block`).
      className="hidden print:block"
      data-testid="print-branding-footer"
      style={{
        position: 'fixed',
        bottom: '4mm',
        left: '8mm',
        right: '8mm',
        borderTop: `2px solid ${accent}`,
        paddingTop: '4px',
        fontFamily: bodyStack,
        fontSize: '9pt',
        color: accent,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {branding.custom_logo_url && (
            <img
              src={branding.custom_logo_url}
              alt={label}
              style={{ height: '18px', width: 'auto', objectFit: 'contain' }}
            />
          )}
          <span
            style={{
              fontWeight: 700,
              color: '#0f172a',
              fontFamily: displayStack,
            }}
            data-testid="print-branding-label"
            data-primary-family={displayPrimary}
          >
            {label}
          </span>
          <span style={{ color: '#94a3b8' }}>· powered by Yabbit VoiceFabric</span>
        </div>
        <span style={{ color: '#94a3b8' }}>Call detail export</span>
      </div>
    </div>
  );
};

export default PrintBrandingFooter;
